import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, Filter } from 'lucide-react';
import PropertyCard from '../components/PropertyCard';
import { usePropiedades } from '../components/PropiedadesContext';

const LoadingSpinner = () => (
  <div className="flex flex-col items-center justify-center min-h-[calc(100vh-6rem)] gap-4">
    <div className="relative">
      <div className="w-16 h-16 border-4 border-blue-200 rounded-full animate-spin">
        <div className="absolute top-0 left-0 w-16 h-16 border-4 border-blue-500 rounded-full animate-pulse border-t-transparent"></div>
      </div>
    </div>
    <p className="text-gray-600 animate-pulse">Cargando alquileres...</p>
  </div>
);

const NoResults = () => (
  <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl shadow-xl">
    <div className="bg-blue-50 w-20 h-20 rounded-full flex items-center justify-center mb-6">
      <Search className="w-10 h-10 text-blue-500" />
    </div>
    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
      No se encontraron alquileres
    </h2>
    <p className="text-gray-600 mb-8 max-w-md text-center">
      No hay alquileres disponibles en esta categoría por el momento
    </p>
  </div>
);

const AlquilerPage = () => {
  const { type } = useParams();
  const navigate = useNavigate();
  const { propiedades, loading, error, searchPropiedades } = usePropiedades();
  const [searchTitle, setSearchTitle] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    precioMin: '',
    precioMax: '',
  });
  const propertiesPerPage = 12;

  // Mapeo de tipos
  const typeMap = {
    'casas': { apiType: 'casa', label: 'Casas en Alquiler' },
    'departamentos': { apiType: 'departamento', label: 'Departamentos en Alquiler' },
    'terrenos': { apiType: 'terreno', label: 'Terrenos en Alquiler' },
    'locales': { apiType: 'local', label: 'Locales Comerciales en Alquiler' }
  };

  useEffect(() => {
    if (!type || !typeMap[type]) {
      console.error('Tipo de propiedad no válido:', type);
      navigate('/');
      return;
    }

    const currentType = typeMap[type].apiType;
    const searchParams = {
      tipo: currentType,
      operacion: 'alquilar',
      ...(filters.precioMin && { precioMin: filters.precioMin }),
      ...(filters.precioMax && { precioMax: filters.precioMax }),
      tabla: 'alquileres'
    };

    console.log('Search params:', searchParams);
    searchPropiedades(searchParams);
  }, [type, filters, searchPropiedades, navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const currentType = typeMap[type]?.apiType;
    
    const searchParams = {
      tipo: currentType,
      operacion: 'alquilar',
      tituloSearch: searchTitle.trim(),
      ...(filters.precioMin && { precioMin: filters.precioMin }),
      ...(filters.precioMax && { precioMax: filters.precioMax }),
      tabla: 'alquileres'
    };

    searchPropiedades(searchParams);
  };

  // Filtrar propiedades por título de búsqueda
  const filteredProperties = propiedades.filter(property =>
    property.titulo.toLowerCase().includes(searchTitle.toLowerCase())
  );

  // Calcular paginación
  const indexOfLastProperty = currentPage * propertiesPerPage;
  const indexOfFirstProperty = indexOfLastProperty - propertiesPerPage;
  const currentProperties = filteredProperties.slice(indexOfFirstProperty, indexOfLastProperty);
  const totalPages = Math.ceil(filteredProperties.length / propertiesPerPage);

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="text-red-500">{error}</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      {/* Hero Section with Search */}
      <section className="relative py-24 bg-gradient-to-br from-blue-600 to-purple-600">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full 
                       filter blur-3xl opacity-10 transform -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-300 rounded-full 
                       filter blur-3xl opacity-10 transform translate-x-1/2 translate-y-1/2" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <h1 className="text-4xl md:text-5xl font-bold text-white text-center mb-8 animate-fadeSlideUp">
            {typeMap[type]?.label || 'Alquileres'}
          </h1>
          
          {/* Search Form */}
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-xl shadow-2xl">
              <form onSubmit={handleSubmit} className="p-6">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-blue-500 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Buscar alquileres por título..."
                      value={searchTitle}
                      onChange={(e) => setSearchTitle(e.target.value)}
                      className="w-full pl-12 pr-4 py-4 rounded-xl border border-gray-200
                               focus:ring-2 focus:ring-blue-500 focus:border-transparent
                               transition-all duration-300"
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setShowFilters(!showFilters)}
                      className="group px-6 py-4 bg-blue-50 text-blue-600 rounded-xl
                               transition-all duration-300 hover:bg-blue-100
                               flex items-center gap-2"
                    >
                      <Filter className="w-5 h-5 transition-transform duration-300 group-hover:rotate-180" />
                      <span>Filtros</span>
                    </button>

                    <button
                      type="submit"
                      className="group px-8 py-4 bg-blue-600 text-white rounded-xl
                               transition-all duration-300 hover:bg-blue-700
                               flex items-center gap-2"
                    >
                      <Search className="w-5 h-5 transition-transform duration-300 group-hover:scale-110" />
                      <span>Buscar</span>
                    </button>
                  </div>
                </div>

                {/* Filters Section */}
                {showFilters && (
                  <div className="mt-6 p-6 bg-gray-50 rounded-xl border border-gray-100
                               animate-slideDown">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Precio mensual mínimo (S/)
                        </label>
                        <input
                          type="number"
                          value={filters.precioMin}
                          onChange={(e) => setFilters({...filters, precioMin: e.target.value})}
                          placeholder="Mínimo"
                          className="w-full px-4 py-3 rounded-xl border border-gray-200
                                   focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Precio mensual máximo (S/)
                        </label>
                        <input
                          type="number"
                          value={filters.precioMax}
                          onChange={(e) => setFilters({...filters, precioMax: e.target.value})}
                          placeholder="Máximo"
                          className="w-full px-4 py-3 rounded-xl border border-gray-200
                                   focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center 
                     space-y-4 sm:space-y-0 mb-8">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 
                      bg-clip-text text-transparent">
            {filteredProperties.length} {typeMap[type]?.label || 'Alquileres'} Encontrados
          </h2>
        </div>

        {currentProperties.length === 0 ? (
          <NoResults />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {currentProperties.map((property, index) => (
              <div
                key={property.id}
                className="transform transition-all duration-500 hover:scale-102"
                style={{
                  opacity: 0,
                  animation: `fadeSlideIn 0.5s ease-out ${index * 0.1}s forwards`
                }}
              >
                <PropertyCard property={property} />
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-12 flex justify-center gap-2">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-4 py-2 rounded-lg transition-all duration-300 
                          ${currentPage === i + 1 ? 'bg-blue-600 text-white'
                            : 'bg-white text-gray-600 hover:bg-blue-50'
                          }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AlquilerPage;